import * as React from 'react';

import { ConfirmingButton, Text } from '@components/common';
import { TextWithCopyButton } from '@components/shared/TextWithCopyButton';
import { useStudyUsers } from '@components/StudiesApp/components/StudyDraft/pages/Calendar/components/NylasCalendar/hooks/useStudyUsers';

interface ModeratorModalBodyProps {
  href: string;
}

const ModeratorModalBody = ({ href }: ModeratorModalBodyProps) => (
  <div>
    <Text className='mb-4'>
      This link leads to the moderator’s interview session. If you continue, you will enter the call and may be seen or
      heard by participants.
    </Text>
    <TextWithCopyButton text={href} />
  </div>
);

interface NonModeratorModalBodyProps {
  href: string;
  location?: 'interview_room' | 'other';
}

const LIVESTREAM_LOCATION = { interview_room: 'directly on this page', other: 'from the Interview Room page' };

const NonModeratorModalBody = ({ href, location = 'other' }: NonModeratorModalBodyProps) => {
  const locationString = LIVESTREAM_LOCATION[location];

  return (
    <div>
      <Text className='mb-4'>
        This link takes you into the live session with the moderator and participant.
        <strong> Only join if you've been invited by the moderator.</strong> If live streaming is enabled, you don't
        need to join the call to watch. The session will stream {locationString}.
      </Text>
      <Text className='mb-4'>You may be seen or heard by participants if you continue.</Text>
      <TextWithCopyButton helperText='Moderator call link' text={href} />
    </div>
  );
};

interface JoinCallButtonProps {
  noStyle?: boolean;
  currentUser: User;
  study?: any;
  href: string;
  children?: React.ReactNode;
  className?: string;
  small?: boolean;
  medium?: boolean;
  location?: 'interview_room' | 'other';
}

export const JoinCallButton = ({
  currentUser,
  study,
  href,
  noStyle = false,
  small = true,
  medium = false,
  children,
  className,
  location = 'other',
  ...rest
}: JoinCallButtonProps) => {
  const { allStudyUsers } = useStudyUsers({
    studyId: study.id,
    ownerId: study.owner_id as any,
    isRoundRobin: study.moderation_style === 'round_robin'
  });

  const isModerator = allStudyUsers?.find(
    (user) => user.studyUser.user_id === currentUser.id && user.studyUser.role === 'moderator'
  );

  return (
    <>
      <ConfirmingButton
        noStyle={noStyle}
        target='_blank'
        dataTestId={'join-call-button'}
        small={small}
        medium={medium}
        icon='videoCamera'
        href={href}
        modalSize='lg'
        danger={!isModerator}
        confirmText={isModerator ? 'Join the moderator call' : 'Join the call anyway'}
        className={className}
        modalTitle={
          isModerator
            ? "You're about to join the moderator call with the participant"
            : 'You’re about to enter the moderator’s interview call'
        }
        modalBody={
          isModerator ? <ModeratorModalBody href={href} /> : <NonModeratorModalBody location={location} href={href} />
        }
        {...rest}
      >
        {children}
      </ConfirmingButton>
    </>
  );
};
